import React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Container, Typography, TextField, Button, Paper, Box, CircularProgress, Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { SETTINGS } from '../../infrastructure/config/api';

const Settings = () => {
  const [baseInvestment, setBaseInvestment] = useState('');
  const [temperature, setTemperature] = useState('');
  const [v17Investment, setV17Investment] = useState('');
  const [roeGuardThreshold, setRoeGuardThreshold] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [user, setUser] = useState(null);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const parsedUser = JSON.parse(userStr);
      setUser(parsedUser);
    }
    setIsCheckingAuth(false);
  }, []);

  const fetchSettings = useCallback(async (userIdToFetch) => {
    if (!userIdToFetch) return;
    setIsLoading(true);
    try {
      const baseInvestmentResponse = await api.get(SETTINGS.BASE_INVESTMENT, { params: { targetUserId: userIdToFetch } });
      setBaseInvestment(baseInvestmentResponse.data.value.toString());
    } catch (error) {
      console.warn('Base Investment setting not found or error fetching, using default.', error);
      setBaseInvestment('0.7'); // Default value
    }

    try {
      const temperatureResponse = await api.get(SETTINGS.TEMPERATURE, { params: { targetUserId: userIdToFetch } });
      setTemperature(temperatureResponse.data.value.toString());
    } catch (error) {
      console.warn('Temperature setting not found or error fetching, using default.', error);
      setTemperature('0.7'); // Default value
    }

    try {
      const v17InvestmentResponse = await api.get(SETTINGS.V17_INVESTMENT, { params: { targetUserId: userIdToFetch } });
      setV17Investment(v17InvestmentResponse.data.value.toString());
    } catch (error) {
      console.warn('V17 Investment setting not found or error fetching, using default.', error);
      setV17Investment('5');
    }

    try {
      const roeThresholdResponse = await api.get(SETTINGS.ROE_GUARD_THRESHOLD, { params: { targetUserId: userIdToFetch } });
      setRoeGuardThreshold(roeThresholdResponse.data.value.toString());
    } catch (error) {
      console.warn('ROE Guard threshold setting not found or error fetching, using default.', error);
      setRoeGuardThreshold('65');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      const fetchUsersAndSettings = async () => {
        if (user.role === 'admin') {
          try {
            const usersResponse = await api.get('/api/account/users');
            setUsers(usersResponse.data.data);
            // Set selectedUserId to the first user or current user if admin
            if (usersResponse.data.data.length > 0) {
              setSelectedUserId(user.id || usersResponse.data.data[0].id);
            }
          } catch (error) {
            console.error('Error fetching users:', error);
            enqueueSnackbar('Failed to load users', { variant: 'error' });
          }
        } else {
          // Non-admin users can only see their own settings
          setSelectedUserId(user.id);
        }
      };
      fetchUsersAndSettings();
    }
  }, [user, enqueueSnackbar]);

  useEffect(() => {
    if (selectedUserId) {
      fetchSettings(selectedUserId);
    }
  }, [selectedUserId, fetchSettings]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(SETTINGS.BASE_INVESTMENT, { value: parseFloat(baseInvestment), targetUserId: selectedUserId });
      await api.put(SETTINGS.TEMPERATURE, { value: parseFloat(temperature), targetUserId: selectedUserId });
      await api.put(SETTINGS.V17_INVESTMENT, { value: parseFloat(v17Investment), targetUserId: selectedUserId });
      await api.put(SETTINGS.ROE_GUARD_THRESHOLD, { value: parseFloat(roeGuardThreshold), targetUserId: selectedUserId });
      enqueueSnackbar('Settings saved successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error saving settings:', error);
      enqueueSnackbar(error.response?.data?.message || 'Failed to save settings', { variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isCheckingAuth) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  }

  if (!user) {
    enqueueSnackbar('You are not authorized to view this page.', { variant: 'error' });
    return <Navigate to="/dashboard" />;
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Settings
        </Typography>

        {user.role === 'admin' && users.length > 0 && (
          <FormControl fullWidth margin="normal">
            <InputLabel id="user-select-label">Select User</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              value={selectedUserId}
              label="Select User"
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={isSaving}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.username} ({u.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isLoading ? (
          <CircularProgress />
        ) : (
          <Box component="form" noValidate autoComplete="off">
            <TextField
              fullWidth
              label="Base Investment"
              variant="outlined"
              value={baseInvestment}
              onChange={(e) => setBaseInvestment(e.target.value)}
              type="number"
              margin="normal"
              helperText="The base investment amount for AI DCA."
              disabled={isSaving}
            />
            <TextField
              fullWidth
              label="V17 Investment (USDT)"
              variant="outlined"
              value={v17Investment}
              onChange={(e) => setV17Investment(e.target.value)}
              type="number"
              margin="normal"
              helperText="Per-user investment amount for V17 strategy."
              disabled={isSaving}
            />
            <TextField
              fullWidth
              label="AI Temperature"
              variant="outlined"
              value={temperature}
              onChange={(e) => setTemperature(e.target.value)}
              type="number"
              margin="normal"
              helperText="Controls the randomness of AI responses. Higher values (e.g., 0.7-1.0) lead to more varied responses, lower values (e.g., 0.1-0.4) to more deterministic ones."
              inputProps={{ step: "0.1", min: "0", max: "1" }}
              disabled={isSaving}
            />
            <TextField
              fullWidth
              label="ROE Guard Threshold (%)"
              variant="outlined"
              value={roeGuardThreshold}
              onChange={(e) => setRoeGuardThreshold(e.target.value)}
              type="number"
              margin="normal"
              helperText="Auto close position when ROE reaches this percentage."
              inputProps={{ step: "1", min: "1", max: "1000" }}
              disabled={isSaving}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={isSaving}
              sx={{ mt: 2 }}
            >
              {isSaving ? <CircularProgress size={24} /> : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/hyperliquid-wallet-monitor')}
              sx={{ mt: 2, ml: 2 }}
            >
              Hyperliquid Wallet Monitor
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Settings;
